export const APS_DEPOSIT_HELD_BY_ROLES = [
    'listingBrokerage',
    'coOpBrokerage',
    'sellerLawyer',
    'buyerLawyer',
    'builder',
    'developer',
] as const;

export type ApsDepositHeldByRole = (typeof APS_DEPOSIT_HELD_BY_ROLES)[number];

export interface ApsOcrEnumOption {
    value: string;
    label: string;
}

export const APS_DEPOSIT_HELD_BY_ROLE_LABELS: Record<ApsDepositHeldByRole, string> = {
    listingBrokerage: "Vendor's broker",
    coOpBrokerage: "Purchaser's broker",
    sellerLawyer: "Vendor's lawyer",
    buyerLawyer: "Purchaser's lawyer",
    builder: 'Builder',
    developer: 'Developer',
};

export const APS_DEPOSIT_HELD_BY_ROLE_OPTIONS: ApsOcrEnumOption[] = APS_DEPOSIT_HELD_BY_ROLES.map(
    (value) => ({
        value,
        label: APS_DEPOSIT_HELD_BY_ROLE_LABELS[value],
    }),
);

export const APS_PROPERTY_TYPE_VALUES = [
    'Detached',
    'Semi-detached',
    'Condominium',
    'Townhouse (condominium)',
    'Townhouse (freehold)',
    'Duplex',
    'Cottage',
    'Mobile Home',
    'Other',
] as const;

export const APS_PROPERTY_TYPE_OPTIONS: ApsOcrEnumOption[] = APS_PROPERTY_TYPE_VALUES.map(
    (value) => ({
        value,
        label: value,
    }),
);

export const APS_HST_TREATMENT_OPTIONS: ApsOcrEnumOption[] = [
    { value: 'includedIn', label: 'Included in' },
    { value: 'inAdditionTo', label: 'In addition to' },
];

export const APS_IRREVOCABLE_BY_VALUES = ['Buyer', 'Seller'] as const;

export const APS_IRREVOCABLE_BY_OPTIONS: ApsOcrEnumOption[] = APS_IRREVOCABLE_BY_VALUES.map(
    (value) => ({
        value,
        label: value,
    }),
);

export const APS_YES_NO_OPTIONS: ApsOcrEnumOption[] = [
    { value: 'Yes', label: 'Yes' },
    { value: 'No', label: 'No' },
];

export const APS_PARTY_COUNT_OPTIONS: ApsOcrEnumOption[] = Array.from(
    { length: 10 },
    (_, index) => {
        const value = String(index + 1);
        return { value, label: value };
    },
);

export interface ApsOcrExtraction {
    // Prefill / UI fields
    purchaserNames: string[];
    purchasersCount: number | null;
    sellerNames: string[];
    sellersCount: number | null;
    propertyAddress: string | null;
    purchasePrice: string | null;
    closingDate: string | null;
    depositAmount: string | null;

    // Document metadata
    formNumber: string | null;
    formRevisionDate: string | null;
    mlsNumber: string | null;
    agreementDate: string | null;
    confirmationOfAcceptanceDate: string | null;

    // Property details
    municipality: string | null;
    province: string | null;
    postalCode: string | null;
    legalDescription: string | null;
    legalDescriptionPartLot: string | null;
    legalDescriptionPlan: string | null;
    legalDescriptionBeingPart: string | null;
    legalDescriptionOnPlan: string | null;
    legalDescriptionParcel: string | null;
    legalDescriptionSection: string | null;
    legalDescriptionEasement: string | null;
    approximateFrontage: string | null;
    approximateDepth: string | null;
    approximateArea: string | null;
    propertyType: string | null;

    // Financial details
    depositHolder: string | null;
    depositHeldByRole: ApsDepositHeldByRole | null;
    depositDue: string | null;
    balancePayableHow: string | null;

    // Key dates / irrevocability
    irrevocableUntilDate: string | null;
    irrevocableUntilTime: string | null;
    irrevocableBy: string | null;
    requisitionDate: string | null;

    // Inclusions / tax
    chattelsIncluded: string | null;
    fixturesExcluded: string | null;
    rentalItems: string | null;
    hstTreatment: string | null;

    // Notices
    buyerNoticeEmail: string | null;
    buyerNoticeFax: string | null;
    sellerNoticeEmail: string | null;
    sellerNoticeFax: string | null;

    // Lawyers
    buyerLawyerName: string | null;
    buyerLawyerFirm: string | null;
    buyerLawyerAddress: string | null;
    buyerLawyerPhone: string | null;
    buyerLawyerEmail: string | null;
    sellerLawyerName: string | null;
    sellerLawyerFirm: string | null;
    sellerLawyerAddress: string | null;
    sellerLawyerPhone: string | null;
    sellerLawyerEmail: string | null;

    // Brokerages / agents
    listingBrokerage: string | null;
    coOpBrokerage: string | null;
    buyerAgentName: string | null;
    sellerAgentName: string | null;
    hasRealEstateAgent: boolean | null;

    // Execution / schedules
    hasSpousalConsent: boolean | null;
    scheduleAPresent: boolean | null;
    scheduleBPresent: boolean | null;
    schedulesAttached: string[];

    // Conditions
    hasFinancingCondition: boolean | null;
    hasInspectionCondition: boolean | null;
    hasSaleOfBuyerPropertyCondition: boolean | null;
    hasStatusCertificateCondition: boolean | null;
    otherConditionsSummary: string | null;

    // Condo extras (Form 101)
    condoCorporationNumber: string | null;
    unitNumber: string | null;
    levelNumber: string | null;
    condoPlanNumber: string | null;
    parkingExclusiveUse: string[];
    lockerExclusiveUse: string[];
    commonExpensesApprox: string | null;
}

export interface ApsClientPrefill {
    firstName: string;
    lastName: string;
    contactEmail: string;
    isPrimary: boolean;
}

export interface ApsClients {
    primary: ApsClientPrefill & { isPrimary: true };
    additional: Array<ApsClientPrefill & { isPrimary: false }>;
}
